import { useMemo, type ReactNode } from 'react';
import { BOARD_SIZE, premiumAt } from '@scrabble-daily/engine';
import type { SerializedBoardDto } from '../../api/puzzles';
import type { StagedPlacement } from '../../game/store';
import { Cell, type CellTile, type CellHighlight } from './Cell';

interface BoardProps {
  board: SerializedBoardDto;
  staged: StagedPlacement[];
  onCellTap?: (row: number, col: number) => void;
  /** Live word-check highlights keyed "row,col" (valid → green, invalid → red). */
  highlights?: Map<string, CellHighlight>;
  /** Lets the game screen wrap each cell in a drop target without coupling Board to react-dnd. */
  cellWrapper?: (row: number, col: number, cell: ReactNode) => ReactNode;
  /** Prefixes cell test ids (e.g. "result-") so a second board on screen doesn't collide. */
  testIdPrefix?: string;
  /** Thumbnail mode: keep premium colours but drop their tiny TW/DL labels. */
  hidePremiumLabels?: boolean;
}

export function Board({
  board,
  staged,
  onCellTap,
  highlights,
  cellWrapper,
  testIdPrefix = '',
  hidePremiumLabels = false,
}: BoardProps) {
  // Merge committed + staged tiles for display; staged renders with its own style.
  const tileMap = useMemo(() => {
    const map = new Map<string, CellTile>();
    for (const p of board.placed) {
      map.set(`${p.row},${p.col}`, { letter: p.letter, is_blank: p.is_blank, staged: false });
    }
    for (const p of staged) {
      map.set(`${p.row},${p.col}`, {
        letter: p.letter || '?',
        is_blank: p.is_blank,
        staged: true,
      });
    }
    return map;
  }, [board, staged]);

  const grid = (
    <div
      role="group"
      aria-label="Scrabble board"
      className="grid w-full touch-none gap-0 rounded-md border-2 border-stone-400 bg-board-bg p-0.5 [container-type:inline-size]"
      style={{ gridTemplateColumns: `repeat(${BOARD_SIZE}, minmax(0, 1fr))` }}
    >
      {Array.from({ length: BOARD_SIZE }, (_, row) =>
        Array.from({ length: BOARD_SIZE }, (_, col) => {
          const cell = (
            <Cell
              key={cellWrapper ? undefined : `${row}-${col}`}
              row={row}
              col={col}
              premium={premiumAt(row, col)}
              tile={tileMap.get(`${row},${col}`) ?? null}
              highlight={highlights?.get(`${row},${col}`) ?? null}
              onTap={onCellTap}
              testIdPrefix={testIdPrefix}
              hidePremiumLabel={hidePremiumLabels}
            />
          );
          return cellWrapper ? (
            <div key={`${row}-${col}`} className="contents">
              {cellWrapper(row, col, cell)}
            </div>
          ) : (
            cell
          );
        }),
      )}
    </div>
  );

  // The board is fixed: it scales responsively to fit the viewport, so there
  // is no pan/zoom (it stayed put on desktop and only got in the way on mobile).
  return grid;
}
